import Accordion from 'react-bootstrap/Accordion';
import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { AccordionHeader } from './AccordionHeader';
import { useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { AffectedPage, AffectedTag, ListItemsTable, ListItemsTableTag } from './ContentViews/ListItemsTable';
import { TagItemShortcuts } from '../TagItemShortcuts/TagItemShortcuts';
import { FindingDetailsModal, FindingDetails } from './FindingDetailsModal';
import { TagWithMetadata, useDocumentTaxonomyConfig } from 'app/api/tag-queries';
import { useTagsByDocument } from 'app/api/tag-queries/tags';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useDownloadReportPackage } from './useDownloadReportPackage';
import { ValidationCategory, ValidationFinding, useRunValidation } from './useRunValidation';
import { useLatestValidation } from 'app/api/validation-queries';

import './Export.scss';

interface ExportProps {
  documentId: string;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
}

interface TagInfo {
  pageId: string;
  pageNumber: number | null;
  sectionSuperId: string;
  sectionName: string;
  conceptQName: string;
}

function buildTagInfoMap(tags: TagWithMetadata[] | undefined): Map<string, TagInfo> {
  const map = new Map<string, TagInfo>();
  if (!tags) return map;
  for (const tag of tags) {
    map.set(tag.id, {
      pageId: tag.pageId,
      pageNumber: tag.metadata?.pageNumber ?? null,
      sectionSuperId: tag.sectionSuperId,
      sectionName: tag.metadata?.sectionName ?? '',
      conceptQName: tag.concept?.name ?? tag.conceptId,
    });
  }
  return map;
}

function buildFactIdMap(tags: TagWithMetadata[] | undefined): Map<string, AffectedPage> {
  const map = new Map<string, AffectedPage>();
  if (!tags) return map;
  for (const tag of tags) {
    map.set(tag.id, {
      pageId: tag.pageId,
      pageNumber: tag.metadata?.pageNumber ?? null,
      sectionSuperId: tag.sectionSuperId,
      sectionName: tag.metadata?.sectionName ?? '',
    });
  }
  return map;
}

function resolveAffectedPages(factIds: string[], factIdMap: Map<string, AffectedPage>): AffectedPage[] {
  const byPageId = new Map<string, AffectedPage>();
  for (const factId of factIds) {
    const info = factIdMap.get(factId);
    if (!info) continue;
    const existing = byPageId.get(info.pageId);
    if (existing) {
      existing.count = (existing.count ?? 1) + 1;
    } else {
      byPageId.set(info.pageId, { ...info, count: 1 });
    }
  }
  return Array.from(byPageId.values());
}

function locationLabelFor(pages: AffectedPage[]): string | undefined {
  if (pages.length === 0) return undefined;
  if (pages.length === 1) {
    const p = pages[0];
    return p.pageNumber != null ? `${i18n('all.tags.page')} ${p.pageNumber}` : p.sectionName || i18n('all.tags.page');
  }
  return `${pages.length} ${i18n('all.tags.tags')}`;
}

function resolveAffectedTags(
  factIds: string[],
  tagInfoMap: Map<string, TagInfo>,
  factDetails: Record<string, Record<string, string>> | undefined
): AffectedTag[] {
  const out: AffectedTag[] = [];
  for (const factId of factIds) {
    const info = tagInfoMap.get(factId);
    if (!info) continue;
    out.push({
      tagId: factId,
      conceptQName: info.conceptQName,
      pageId: info.pageId,
      pageNumber: info.pageNumber,
      sectionSuperId: info.sectionSuperId,
      sectionName: info.sectionName,
      details: factDetails?.[factId],
    });
  }
  return out;
}

function findingToTag(finding: ValidationFinding, factIdMap: Map<string, AffectedPage>, tagInfoMap: Map<string, TagInfo>): ListItemsTableTag {
  const affectedPages = resolveAffectedPages(finding.factIds, factIdMap);
  const affectedTags = resolveAffectedTags(finding.factIds, tagInfoMap, finding.factDetails);
  return {
    id: finding.code,
    label: finding.tag ?? finding.code.split(/[.:]/).pop() ?? finding.code,
    type: finding.type,
    locationLabel: locationLabelFor(affectedPages),
    status: finding.severity,
    statusInfo: finding.message,
    affectedCount: finding.affectedCount,
    affectedPages,
    affectedTags,
    code: finding.code,
  };
}

function detailsLabel(category: ValidationCategory): string | undefined {
  const { errors, warnings } = category.summary;
  const parts: string[] = [];
  if (errors > 0) {
    parts.push(`${errors} ${i18n(errors === 1 ? 'tag.validation.error' : 'tag.validation.errors')}`);
  }
  if (warnings > 0) {
    parts.push(`${warnings} ${i18n(warnings === 1 ? 'tag.validation.warning' : 'tag.validation.warnings')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function categoryResultForUi(category: ValidationCategory): string {
  return category.result;
}

function formatRanAt(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export const Export = ({ documentId, onTagDetailsClick }: ExportProps) => {
  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('asc');
  const history = useHistory();
  const taggingMatch = useRouteMatch();
  const documentBaseUrl = taggingMatch.url.replace(/\/tagging(\/.*)?$/, '');
  const documentRevisionId = useEditorSelector((editor) => editor?.pDocument?.revisionId as string | undefined);
  const documentLocale = useEditorSelector((editor) => editor?.locale as string | undefined);
  const documentSuperIdInt = parseInt(documentId, 10);
  const { data: tagsResponse } = useTagsByDocument(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt, {
    include: ['metadata', 'concept'],
  });
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt);
  const hasActiveTaxonomy = !!taxonomyConfig?.taxonomySelections?.some((t) => t.isActive);
  const factIdMap = useMemo(() => buildFactIdMap(tagsResponse?.tags), [tagsResponse]);
  const tagInfoMap = useMemo(() => buildTagInfoMap(tagsResponse?.tags), [tagsResponse]);
  const { download: handleDownloadReportPackage, isDownloading } = useDownloadReportPackage({
    documentSuperId: documentId,
    documentRevisionId,
    documentLocale,
  });
  const {
    run: handleRunValidation,
    isRunning: isValidating,
    result: runResult,
  } = useRunValidation({
    documentSuperId: documentId,
    documentRevisionId,
  });
  const { data: latestValidation } = useLatestValidation(Number.isNaN(documentSuperIdInt) ? undefined : documentSuperIdInt);
  const result = runResult ?? latestValidation?.latest?.result;
  const [detailsModal, setDetailsModal] = useState<{ show: boolean; finding: FindingDetails | null }>({
    show: false,
    finding: null,
  });

  const openFindingDetails = (tag: ListItemsTableTag) => {
    setDetailsModal({
      show: true,
      finding: {
        title: tag.label,
        type: tag.type,
        status: tag.status,
        message: tag.statusInfo,
        code: tag.code ?? tag.id,
        affectedCount: tag.affectedCount,
        affectedPages: tag.affectedPages,
        affectedTags: tag.affectedTags,
      },
    });
  };
  const closeFindingDetails = () => setDetailsModal({ show: false, finding: null });

  const handleNavigateToPage = (page: AffectedPage) => {
    history.push({
      pathname: `${documentBaseUrl}/sections/${page.sectionSuperId}`,
      search: '?taggingMode=true',
      hash: page.pageId,
    });
    closeFindingDetails();
  };

  const handleNavigateToTag = (tag: AffectedTag) => {
    history.push({
      pathname: `${documentBaseUrl}/sections/${tag.sectionSuperId}`,
      search: '?taggingMode=true',
      hash: tag.pageId,
    });
    closeFindingDetails();
  };

  const validationStatus = result?.status ?? 'not-run';

  const handleSortToggle = () => {
    setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleRunTest = () => {
    console.log(`Run test clicked`);
  };

  const sortedCategories = useMemo(
    () => (result?.categories ?? []).slice().sort((a, b) => (sortBy === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))),
    [result, sortBy]
  );

  return (
    <div className="export">
      <Typography variant="body-8" fontWeight="semibold">
        {i18n('tag.export.document.label')}
      </Typography>

      <div className="export__actions">
        <button
          className="btn btn-primary"
          onClick={handleDownloadReportPackage}
          disabled={isDownloading || !hasActiveTaxonomy}
          aria-busy={isDownloading}
          title={!hasActiveTaxonomy ? i18n('tag.categories.noActiveTaxonomy') || 'No active taxonomy selected.' : undefined}
        >
          {isDownloading && <i className="fa-regular fa-spinner fa-spin me-2" aria-hidden="true" />}
          {i18n(isDownloading ? 'tag.export.action.generating' : 'tag.export.action.generate')}
        </button>
        <button className="btn btn-secondary" disabled={true}>
          {i18n('tag.export.action.export')}
        </button>
      </div>

      <Typography variant="body-8" fontWeight="semibold">
        {i18n('tag.validation.label')}
      </Typography>

      <div className="export__validation">
        <div className="export__validation--left-container">
          <span className={`export__validation--status export__validation--${validationStatus}`}>{i18n(`tag.validation.status.${validationStatus}`)}</span>
          <Typography variant="body-7" color="#59555F">
            {validationStatus === 'failed'
              ? i18n('tag.validation.with.errors')
              : validationStatus === 'passed-with-warnings'
              ? i18n('tag.validation.with.warnings')
              : validationStatus === 'passed'
              ? i18n('tag.validation.no.warnings')
              : i18n('tag.validation.not.run')}
          </Typography>
        </div>

        <div className="export__validation--right-container">
          <button className="export__validation--btn" onClick={handleRunValidation} disabled={isValidating} aria-busy={isValidating}>
            {isValidating && <i className="fa-regular fa-spinner fa-spin me-2" aria-hidden="true" />}
            {i18n('tag.export.action.validate')}
          </button>
          {result && (
            <Typography variant="body-5" color="#59555F">
              {i18n('tag.validation.last.checked')} {formatRanAt(result.ranAt)}
            </Typography>
          )}
        </div>
      </div>

      <section className="export-report">
        <div className="export-report__header">
          <div className="export-report__header--with-sort" onClick={handleSortToggle}>
            <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
              {i18n('tag.validation.check')}
            </Typography>
            <i className={`fa-solid fa-arrow-down ${sortBy === 'desc' ? 'rotate-180' : ''}`} />
          </div>
          <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
            {i18n('tag.validation.result')}
          </Typography>
          <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
            {i18n('tag.validation.details')}
          </Typography>
        </div>

        <Accordion flush>
          {sortedCategories.map((category, index) => (
            <Accordion.Item key={category.id} eventKey={String(index)}>
              <div className="export-report__item-header">
                <Accordion.Header>
                  <i className="fa-solid fa-lg fa-chevron-right export-report__chevron" />
                  <AccordionHeader title={category.name} result={categoryResultForUi(category)} details={detailsLabel(category)} />
                </Accordion.Header>
                <TagItemShortcuts actions={[{ type: 'test', onClick: handleRunTest }]} />
              </div>
              <Accordion.Body>
                <ListItemsTable
                  tags={category.findings.map((f) => findingToTag(f, factIdMap, tagInfoMap))}
                  onTagDetailsClick={onTagDetailsClick}
                  onFindingDetailsClick={openFindingDetails}
                  onNavigateToPage={handleNavigateToPage}
                />
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </section>

      <FindingDetailsModal
        show={detailsModal.show}
        finding={detailsModal.finding}
        onClose={closeFindingDetails}
        onNavigateToPage={handleNavigateToPage}
        onNavigateToTag={handleNavigateToTag}
      />
    </div>
  );
};
